import { ChevronDown } from "lucide-react";
import React, { useContext, useState } from "react";
import { Badge, Menu, Text } from "@mantine/core";
import { Navigate } from "react-router-dom";
import SurveyList from "../services/model/SurveyList";
import { dateOnlyFormatYY } from "../../../utils/DateTime";
import { badgeConfig } from "../../../utils/Theme";
import SurveyTableMenu from "./SurveyTableMenu";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import useListSurveyApiService from "../services/api/SurveyListApiService";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import SurveyTableEmptyStateContainer from "./SurveyTableEmptyStateContainer";
import { AuthContext } from "../../../providers/AuthProvider";
import Pagination from "../../../themes/components/Pagination";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";

type TargetingMenuProps = {
  label: string;
  items: { id: number; name: string }[];
  emptyText: string;
};

const TargetingMenu: React.FC<TargetingMenuProps> = ({
  label,
  items,
  emptyText,
}) => {
  if (items && items.length > 0) {
    return (
      <Menu
        shadow="md"
        position="bottom"
        control={
          <button
            type="button"
            style={{
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              cursor: "pointer",
              padding: "8px 12px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "120px",
              color: "#495057",
            }}
          >
            <span>View all</span>
            <ChevronDown size={16} />
          </button>
        }
        styles={{
          body: { padding: "8px 0", width: "220px" },
          label: {
            fontSize: "14px",
            fontWeight: 600,
            color: "#495057",
            padding: "8px 16px 4px",
          },
          item: { fontSize: "14px", padding: "10px 16px", color: "#495057" },
        }}
      >
        <Menu.Label>{label}</Menu.Label>
        {items.map((item) => (
          <Menu.Item key={item.id}>{item.name}</Menu.Item>
        ))}
      </Menu>
    );
  }
  return <Text size="xs">{emptyText}</Text>;
};

interface SurveyTableProps {
  selection: SurveyList[];
  onSelection: React.Dispatch<React.SetStateAction<SurveyList[]>>;
}

const SurveyTable: React.FC<SurveyTableProps> = ({
  onSelection,
  selection,
}) => {
  const auth = useContext(AuthContext);
  const tableMaxHeight = ResponsiveHeight(
    "",
    "80vh",
    "80vh",
    "80vh",
    "75vh",
    "80vh"
  );

  const [activePage, setActivePage] = useState(1);

  const { isLoading, isError, error, data } = useListSurveyApiService({
    page: activePage,
    status: "",
    employer_id: auth?.employer.id ?? 0,
    pagination: true,
  });

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!data || data.results.length === 0) {
    return <SurveyTableEmptyStateContainer />;
  }

  return (
    <>
      <DirectoryTable<SurveyList>
        tableMaxHeight={tableMaxHeight}
        data={data.results}
        headers={[
          "Survey Name",
          "Created By",
          "Responses",
          "Launch Date",
          "Close Date",
          "Tags/Segments",
          "Status",
          "",
        ]}
        tableHorizontalSpacing="xs"
        onSelect={onSelection}
        rows={data.results.map((it) => ({
          title:
            it.title.length <= 30
              ? it.title
              : `${it.title.substring(0, 30)}...`,
          created_by: (
            <Text size="sm">
              {it.created_by?.first_name} {it.created_by?.last_name[0]}
            </Text>
          ),
          form_stats: `${it.form_stats.unique_response_count}/${
            it.audience_size ?? "-"
          }`,
          scheduled_start_date: it.scheduled_start_date
            ? dateOnlyFormatYY(it.scheduled_start_date)
            : "-",
          scheduled_end_date: `${
            it.scheduled_end_date
              ? dateOnlyFormatYY(it.scheduled_end_date)
              : "-"
          }`,
          tags_segments: (
            <div className="flex flex-col gap-2">
              {it.targeting_type === "segments" ? (
                <TargetingMenu
                  label="Segments"
                  items={it.segments || []}
                  emptyText="-"
                />
              ) : (
                <TargetingMenu label="Tags" items={[]} emptyText="-" />
              )}
            </div>
          ),
          status: (
            <Badge
              style={{
                color: badgeConfig[it.form_state || ""],
                backgroundColor: badgeConfig[it.form_state || ""],
              }}
            >
              {it.form_state || ""}
            </Badge>
          ),
          actions: (
            <SurveyTableMenu
              id={it.id}
              title={it.title}
              status={it.status || ""}
            />
          ),
        }))}
        selection={selection}
      />

      {data.pages > 1 && (
        <Pagination
          onChange={setActivePage}
          total={data.pages}
          initialPage={1}
        />
      )}
    </>
  );
};

export default SurveyTable;
